import {
  pendenciasDeComissao, paraAlvoPendente, regraPagaZeroEmSilencio, type AlvoPendente,
} from './completude'
import type { ComissaoPercentual, Produto, RegraComissao } from '../db/tipos'
import { banco } from '../db/banco'
import { escolherRegra, pctExplicitoParaProduto, pctParaProduto } from './motor'
import { produtosComPrecoNaTabela } from './produtosVendaveis'

export interface PlanoComissaoTabela {
  // true so quando TAMBEM ha pendentes: tabela sem preco nenhum nao ganha regra
  criarRegra: boolean
  // vazio = tabela coberta, nada a fazer
  pendentes: AlvoPendente[]
  // Defeito da REGRA, nao dos produtos: uma pct_fixo sem percentual faz TUDO
  // que ela governa pagar 0% em silencio. Nao entra em `pendentes` porque
  // percentual por produto e inerte numa pct_fixo -- o unico remedio e digitar
  // o % na regra, na tela da Marca.
  regraSemPercentual: boolean
}

// Dado o estado de UMA tabela, decide o que falta para nenhum produto dela ser
// vendido a 0% em silencio. Pura de proposito: toda a consulta ao Dexie fica
// fora, em avisosDeComissaoPorTabela e na tela.
export function planoDeComissaoDaTabela(
  regra: RegraComissao | undefined,
  percentuais: ComissaoPercentual[],
  produtosComPreco: Produto[],
): PlanoComissaoTabela {
  // Sem regra nenhuma governando a tabela, todo produto precificado esta
  // descoberto -- e precisamos de uma regra para pendurar os percentuais.
  if (!regra) {
    const pendentes = produtosComPreco.map(paraAlvoPendente)
    return { criarRegra: pendentes.length > 0, pendentes, regraSemPercentual: false }
  }
  return {
    criarRegra: false,
    pendentes: pendenciasDeComissao(regra, percentuais, produtosComPreco),
    // Sem produto precificado a tabela nao paga nada a ninguem: nao ha zero
    // silencioso, e um alarme ali so treinaria a usuaria a ignorar o aviso.
    regraSemPercentual: produtosComPreco.length > 0 && regraPagaZeroEmSilencio(regra),
  }
}

// Consulta o plano de UMA tabela agora: busca a regra governante, os percentuais
// dela e os produtos vendaveis, e devolve o plano ja calculado + o id da regra
// (ou null quando nao ha nenhuma -- exatamente quando plano.criarRegra pode ser
// true). Usada pela tela de precos (concluir e a faixa de aviso da lista); ver
// nota em avisosDeComissaoPorTabela sobre por que o laco NAO reusa esta funcao.
export async function planoDaTabelaAgora(
  representadaId: string,
  tabelaId: string,
): Promise<{ plano: PlanoComissaoTabela; regraId: string | null }> {
  const regras = await banco.regras_comissao
    .where('representada_id').equals(representadaId)
    .filter((r) => r.apagado_em === null)
    .toArray()
  const regra = escolherRegra(regras, tabelaId)
  const percentuais = regra
    ? await banco.comissao_percentuais
        .where('regra_id').equals(regra.id)
        .filter((p) => p.apagado_em === null)
        .toArray()
    : []
  const produtos = await produtosComPrecoNaTabela(representadaId, tabelaId)
  const plano = planoDeComissaoDaTabela(regra, percentuais, produtos)
  return { plano, regraId: regra?.id ?? null }
}

export interface AvisoComissaoTabela {
  pendentes: number
  regraSemPercentual: boolean
  // Para a FAIXA do card (28/09): o % explicito de cada produto da tabela e
  // se algum cai no padrao. So vale para a regra SALVA que a tabela usa.
  regraId: string | null
  explicitos: number[]
  algumSemExplicito: boolean
}

// O que cada tabela da marca tem de errado na comissao hoje. Alimenta o selo
// da tela da Marca -- e o que impede a pendencia de sumir quando a usuaria sai
// da tela de precos sem concluir.
//
// Devolve os DOIS avisos porque eles tem remedios diferentes: `pendentes` se
// resolve produto a produto (passo "Concluir tabela"); `regraSemPercentual` so
// se resolve digitando o % na regra, na propria tela da Marca.
//
// Nao reusa planoDaTabelaAgora aqui de proposito: esta funcao busca as regras da
// marca UMA vez fora do laco (ela roda para TODAS as tabelas da marca a cada
// render da tela da Marca); planoDaTabelaAgora busca de novo a cada chamada, o
// que trocaria 1 consulta por N. A logica de cada iteracao e a MESMA de
// planoDaTabelaAgora (escolherRegra -> percentuais -> produtosComPrecoNaTabela
// -> planoDeComissaoDaTabela); ver contarPendencias.test.ts.
export async function avisosDeComissaoPorTabela(
  representadaId: string,
): Promise<Map<string, AvisoComissaoTabela>> {
  const tabelas = await banco.tabelas_preco
    .where('representada_id').equals(representadaId)
    .filter((t) => t.apagado_em === null)
    .toArray()
  const regras = await banco.regras_comissao
    .where('representada_id').equals(representadaId)
    .filter((r) => r.apagado_em === null)
    .toArray()

  const avisos = new Map<string, AvisoComissaoTabela>()
  for (const tabela of tabelas) {
    const regra = escolherRegra(regras, tabela.id)
    const percentuais = regra
      ? await banco.comissao_percentuais
          .where('regra_id').equals(regra.id)
          .filter((p) => p.apagado_em === null)
          .toArray()
      : []
    const produtos = await produtosComPrecoNaTabela(representadaId, tabela.id)
    const plano = planoDeComissaoDaTabela(regra, percentuais, produtos)
    const explicitos: number[] = []
    let algumSemExplicito = produtos.length === 0
    for (const p of produtos) {
      const e = regra ? pctExplicitoParaProduto(regra, percentuais, p) : null
      if (e === null) algumSemExplicito = true
      else explicitos.push(e)
    }
    avisos.set(tabela.id, {
      // o card diz "N sem comissão": o que o MOTOR pagaria como nada (teste dela 01/10)
      pendentes: produtosSemComissaoNoMotor(regra, percentuais, produtos),
      regraSemPercentual: plano.regraSemPercentual,
      regraId: regra?.id ?? null,
      explicitos,
      algumSemExplicito,
    })
  }
  return avisos
}

// O "N sem comissão" do card da tabela (teste dela 01/10): conta o que o MOTOR
// pagaria como nada -- sem regra, ou sem % proprio E sem % padrao. Antes o card
// usava `pendentes` (sem % proprio), que ignora o % padrao que o motor paga, e
// avisava "15 sem comissão" numa tabela em que todos recebem.
export function produtosSemComissaoNoMotor(
  regra: RegraComissao | undefined, percentuais: ComissaoPercentual[], produtos: Produto[],
): number {
  if (!regra) return produtos.length
  // % fixo vazio: o card ja acusa a REGRA ("comissão sem % — paga 0%"), sem contar
  // produto a produto de novo.
  if (regra.tipo === 'pct_fixo') return 0
  return produtos.filter((p) => pctParaProduto(regra, percentuais, p) == null).length
}
